"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Plus, Star } from "lucide-react";
import { toast } from "sonner";
import { useSiteAuthStore } from "@gerama/stores";
import { cn } from "@gerama/utils";
import { browserCoreApi } from "@/lib/api";
import { openLogin } from "@/features/auth/login-modal";
import { invalidateLibrary } from "@/features/discover/library-keys";

export const followKeys = {
  status: (artistId: number, signedIn: boolean) =>
    ["artists", "follow", artistId, signedIn] as const,
};

/** The follow state of one artist (count for anyone, `following` when signed in) and a toggle that keeps every follow button in sync. */
export function useArtistFollow(
  artistId: number,
  initial?: { following: boolean; followerCount: number },
) {
  const token = useSiteAuthStore((s) => s.token);
  const client = useQueryClient();
  const key = followKeys.status(artistId, !!token);

  const status = useQuery({
    queryKey: key,
    queryFn: ({ signal }) => browserCoreApi.playback.artistFollowStatus(artistId, signal),
    initialData: initial
      ? { following: initial.following, followerCount: initial.followerCount }
      : undefined,
    staleTime: 60_000,
  });
  const following = !!status.data?.following;

  const toggle = useMutation({
    mutationFn: () =>
      following
        ? browserCoreApi.playback.unfollowArtist(artistId)
        : browserCoreApi.playback.followArtist(artistId),
    onMutate: () => {
      const previous = client.getQueryData<{ following: boolean; followerCount: number }>(key);
      client.setQueryData(key, {
        following: !following,
        followerCount: Math.max(0, (previous?.followerCount ?? 0) + (following ? -1 : 1)),
      });
      return { previous };
    },
    onSuccess: (result) => {
      if (result)
        client.setQueryData(key, {
          following: result.following ?? !following,
          followerCount: result.followerCount,
        });
      void invalidateLibrary(client);
    },
    onError: (_error, _vars, context) => {
      if (context?.previous) client.setQueryData(key, context.previous);
      toast.error("انجام نشد");
    },
  });

  return {
    following,
    followerCount: status.data?.followerCount ?? 0,
    pending: toggle.isPending,
    toggle: () => {
      if (!token) {
        openLogin();
        return;
      }
      toggle.mutate();
    },
  };
}

/** Follow / following pill (the artist page header) or a bare star (the song page's artist card). */
export function FollowArtistButton({
  artistId,
  variant = "pill",
  className,
}: {
  artistId: number;
  variant?: "pill" | "star";
  className?: string;
}) {
  const { following, pending, toggle } = useArtistFollow(artistId);
  const label = following ? "دنبال می‌کنی" : "دنبال کردن";

  if (variant === "star") {
    return (
      <button
        type="button"
        onClick={toggle}
        disabled={pending}
        aria-pressed={following}
        aria-label={label}
        title={label}
        className={cn(
          "flex size-9 items-center justify-center rounded-full transition-colors",
          following ? "text-primary" : "text-muted-foreground hover:text-foreground",
          className,
        )}
      >
        <Star className={cn("size-5", following && "fill-current")} />
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={toggle}
      disabled={pending}
      aria-pressed={following}
      className={cn(
        "flex h-10 items-center gap-2 rounded-full border px-5 text-sm font-bold transition-colors",
        following
          ? "border-foreground/30 bg-foreground/10"
          : "border-foreground/60 hover:border-foreground hover:bg-foreground/10",
        className,
      )}
    >
      {following ? <Check className="size-4" /> : <Plus className="size-4" />}
      {label}
    </button>
  );
}
